import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import * as WebBrowser from 'expo-web-browser';
import {
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Share2,
  ShieldCheck,
  X,
} from 'lucide-react-native';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';

import { useNotice } from '@/components/ConfirmDialog';
import { DocPageFull } from '@/components/DocPagePreview';
import { getNativePdf } from '@/components/NativePdf';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { readUriAsBase64 } from '@/lib/files';
import type { DocumentRow } from '@/db/types';
import { DOCUMENT_KIND_LABEL } from '@/lib/documents';
import { isImageMime, isPdfMime } from '@/lib/files';

/**
 * Full-screen preview for a document — image inline at full size, PDF in an
 * embedded iframe on web (browsers handle PDF rendering natively), or
 * "Open externally" for other types. The metadata header sits above the
 * preview area so users can confirm what they're looking at without
 * leaving the app.
 */
export function DocumentPreview({
  doc,
  systemLabel,
  onClose,
}: {
  doc: DocumentRow | null;
  systemLabel?: string | null;
  onClose: () => void;
}) {
  const open = doc != null;
  const notice = useNotice();
  // Desktop gets a document-sized reading pane (Wave 52) — a 480px
  // column wastes a monitor when the content is a full-page PDF.
  const { width: winW } = useWindowDimensions();
  const isWide = winW >= 768;

  /** Android true-save (Wave 55): user picks a folder once (Storage
   *  Access Framework), the file's bytes are written there — a real
   *  download, not a share sheet. iOS has no user-facing filesystem
   *  outside the share sheet, so Save routes there by convention. */
  const saveToDevice = async () => {
    if (!doc) return;
    try {
      if (Platform.OS !== 'android') {
        if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(doc.file_uri);
        return;
      }
      const base64 = await readUriAsBase64(doc.file_uri);
      if (!base64) throw new Error('could not read file data');
      const ext = isPdfMime(doc.mime_type)
        ? '.pdf'
        : isImageMime(doc.mime_type)
        ? (doc.mime_type?.includes('png') ? '.png' : '.jpg')
        : '';
      const safeName =
        (doc.title.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'homestead-file') + ext;
      const mime = doc.mime_type ?? 'application/octet-stream';

      // Wave 55.2: Android's CREATE_DOCUMENT "save file" dialog — the
      // same one browsers use for downloads. It DEFAULTS to Downloads,
      // lets the user rename, and — unlike the folder-tree picker —
      // is allowed to write there ("Can't use this folder" was the
      // tree picker refusing the Downloads root on Android 11+).
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const IntentLauncher = require('expo-intent-launcher');
        const result = await IntentLauncher.startActivityAsync(
          'android.intent.action.CREATE_DOCUMENT',
          {
            category: 'android.intent.category.OPENABLE',
            type: mime,
            extra: { 'android.intent.extra.TITLE': safeName },
          }
        );
        if (result.resultCode === -1 && result.data) {
          await FileSystem.writeAsStringAsync(result.data, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          void notice({
            title: 'Saved',
            message: `${safeName} was saved to the location you chose.`,
            tone: 'info',
          });
        }
        return;
      } catch (err) {
        console.warn('CREATE_DOCUMENT unavailable, trying folder flow', err);
      }

      // Older builds without the intent module: folder-tree picker
      // (user must choose a SUBFOLDER — Android refuses the Downloads
      // root here), then share sheet as the last resort.
      const saf = FileSystem.StorageAccessFramework;
      if (saf?.requestDirectoryPermissionsAsync) {
        const perm = await saf.requestDirectoryPermissionsAsync();
        if (!perm.granted) return;
        const target = await saf.createFileAsync(perm.directoryUri, safeName, mime);
        await FileSystem.writeAsStringAsync(target, base64, {
          encoding: FileSystem.EncodingType.Base64,
        });
        void notice({
          title: 'Saved to your device',
          message: `${safeName} is in the folder you picked.`,
          tone: 'info',
        });
        return;
      }
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(doc.file_uri);
    } catch (err) {
      console.warn('save to device failed', err);
      void notice({ title: "Couldn't save the file", message: String(err), tone: 'error' });
    }
  };

  const shareFile = async () => {
    if (!doc) return;
    try {
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(doc.file_uri);
    } catch (err) {
      console.warn('share failed', err);
    }
  };

  const openExternally = async () => {
    if (!doc) return;
    try {
      if (Platform.OS === 'web') {
        // Chrome refuses top-level data: navigation — rewrap inlined
        // files (Wave 54 web storage) as a temporary object URL.
        let target = doc.file_uri;
        if (target.startsWith('data:')) {
          // eslint-disable-next-line no-undef
          const blob = await (await fetch(target)).blob();
          // eslint-disable-next-line no-undef
          target = URL.createObjectURL(blob);
        }
        // eslint-disable-next-line no-undef
        window.open(target, '_blank', 'noopener,noreferrer');
        return;
      }
      // http(s) files (future cloud storage) open in the browser fine.
      if (/^https?:/.test(doc.file_uri)) {
        await WebBrowser.openBrowserAsync(doc.file_uri);
        return;
      }
      // Local file on Android: a browser tab can't read file:// — the
      // old code silently died here (Wave 55.1 user report). The right
      // move is an ACTION_VIEW intent with a readable content:// URI;
      // the intent-launcher module ships in build v4+, older builds
      // fall through to the share sheet (which can also open apps).
      if (Platform.OS === 'android') {
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          const IntentLauncher = require('expo-intent-launcher');
          const contentUri = await FileSystem.getContentUriAsync(doc.file_uri);
          await IntentLauncher.startActivityAsync('android.intent.action.VIEW', {
            data: contentUri,
            flags: 1, // FLAG_GRANT_READ_URI_PERMISSION
            type: doc.mime_type ?? undefined,
          });
          return;
        } catch (err) {
          console.warn('VIEW intent unavailable, falling back to share', err);
        }
      }
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(doc.file_uri);
      }
    } catch (err) {
      console.warn('open document failed', err);
      void notice({
        title: "Couldn't open the file",
        message: 'Try Share instead — any app that supports this file type can receive it.',
        tone: 'warning',
      });
    }
  };

  // True download: web triggers the browser's save-file flow with a
  // sensible filename; native opens the share sheet (which includes
  // "Save to Files" / "Save image"). Every scanned receipt, invoice,
  // and estimate is a real file the user can take with them (Wave 47).
  const download = async () => {
    if (!doc) return;
    try {
      if (Platform.OS === 'web') {
        const ext = isPdfMime(doc.mime_type)
          ? '.pdf'
          : isImageMime(doc.mime_type)
          ? (doc.mime_type?.includes('png') ? '.png' : '.jpg')
          : '';
        const safeName =
          doc.title.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'homestead-file';
        // eslint-disable-next-line no-undef
        const a = document.createElement('a');
        a.href = doc.file_uri;
        a.download = `${safeName}${ext}`;
        // eslint-disable-next-line no-undef
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(doc.file_uri);
      }
    } catch (err) {
      console.warn('download document failed', err);
    }
  };

  if (!doc) return null;

  const isImage = isImageMime(doc.mime_type);
  const isPdf = isPdfMime(doc.mime_type);

  return (
    <Modal animationType="fade" transparent visible={open} onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={[styles.sheet, isWide && styles.sheetWide]}>
          <View style={styles.head}>
            <View style={[styles.kindIcon, { backgroundColor: isImage ? '#DCE7E1' : '#EAF0EA' }]}>
              {isImage ? (
                <ImageIcon size={18} color={Palette.forest} strokeWidth={1.8} />
              ) : (
                <FileText size={18} color={Palette.forest} strokeWidth={1.8} />
              )}
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.title} numberOfLines={1}>{doc.title}</Text>
              <View style={styles.metaRow}>
                <View style={styles.metaPill}>
                  <Text style={styles.metaPillText}>{DOCUMENT_KIND_LABEL[doc.kind]}</Text>
                </View>
                {doc.cost_cents != null ? (
                  <View style={[styles.metaPill, { backgroundColor: '#F4E9D4' }]}>
                    <Text style={[styles.metaPillText, { color: '#8A5A16' }]}>
                      ${(doc.cost_cents / 100).toLocaleString()}
                    </Text>
                  </View>
                ) : null}
                <Text style={styles.metaText} numberOfLines={1}>
                  {systemLabel ?? 'Property-wide'}
                </Text>
              </View>
              {doc.notes && !doc.title.includes(doc.notes) ? (
                <Text style={styles.notesText} numberOfLines={1}>{doc.notes}</Text>
              ) : null}
            </View>
            <Pressable onPress={onClose} hitSlop={10} style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          {doc.expires_at ? (
            <View style={styles.expiresRow}>
              <ShieldCheck size={12} color={Palette.honeyDeep} strokeWidth={2} />
              <Text style={styles.expiresText}>
                Expires {new Date(doc.expires_at).toLocaleDateString()}
              </Text>
            </View>
          ) : null}

          <View style={styles.previewArea}>
            {isImage ? (
              <ScrollView
                contentContainerStyle={styles.imageScrollContent}
                maximumZoomScale={3}
                minimumZoomScale={1}
                bouncesZoom>
                <Image source={{ uri: doc.file_uri }} style={styles.image} resizeMode="contain" />
              </ScrollView>
            ) : isPdf && Platform.OS === 'web' ? (
              <PdfWebPreview uri={doc.file_uri} />
            ) : isPdf ? (
              <PdfNativePreview uri={doc.file_uri} />
            ) : (
              // Text-bearing documents (docx/xlsx/pptx/txt/csv/eml/…)
              // render their extracted text as a readable page (Wave
              // 56.1); truly opaque formats keep the open-externally
              // fallback.
              <DocPageFull
                uri={doc.file_uri}
                mime={doc.mime_type}
                name={doc.file_uri}
                fallback={
                  <View style={styles.fallback}>
                    <FileText size={42} color={Palette.inkFaint} strokeWidth={1.4} />
                    <Text style={styles.fallbackTitle}>Preview not available here</Text>
                    <Text style={styles.fallbackBody}>
                      Tap &quot;Open&quot; to view this file externally.
                    </Text>
                  </View>
                }
              />
            )}
          </View>

          <View style={styles.footer}>
            {Platform.OS === 'web' ? (
              <>
                <Pressable onPress={download} style={({ pressed }) => [styles.footerBtn, pressed && { opacity: 0.85 }]}>
                  <Download size={16} color={Palette.cream} strokeWidth={2} />
                  <Text style={styles.footerBtnText}>Download</Text>
                </Pressable>
                <Pressable onPress={openExternally} style={({ pressed }) => [styles.footerBtn, styles.footerBtnOutline, pressed && { opacity: 0.85 }]}>
                  <ExternalLink size={16} color={Palette.forest} strokeWidth={2} />
                  <Text style={[styles.footerBtnText, { color: Palette.forest }]}>Open</Text>
                </Pressable>
              </>
            ) : (
              // Wave 55 (user request): distinct Save / Share / Open on
              // mobile — Save is a true download to a picked folder on
              // Android, Share opens the share sheet, Open hands off to
              // the system viewer.
              <>
                <Pressable onPress={() => void saveToDevice()} style={({ pressed }) => [styles.footerBtn, pressed && { opacity: 0.85 }]}>
                  <Download size={16} color={Palette.cream} strokeWidth={2} />
                  <Text style={styles.footerBtnText}>Save</Text>
                </Pressable>
                <Pressable onPress={() => void shareFile()} style={({ pressed }) => [styles.footerBtn, styles.footerBtnOutline, pressed && { opacity: 0.85 }]}>
                  <Share2 size={16} color={Palette.forest} strokeWidth={2} />
                  <Text style={[styles.footerBtnText, { color: Palette.forest }]}>Share</Text>
                </Pressable>
                <Pressable onPress={openExternally} style={({ pressed }) => [styles.footerBtn, styles.footerBtnOutline, pressed && { opacity: 0.85 }]}>
                  <ExternalLink size={16} color={Palette.forest} strokeWidth={2} />
                  <Text style={[styles.footerBtnText, { color: Palette.forest }]}>Open</Text>
                </Pressable>
              </>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Inline scrollable PDF on Android/iOS (Wave 53) via react-native-pdf.
 * The native view ships with the next eas build; on older builds the
 * module probe fails and we show the open-externally fallback.
 */
function PdfNativePreview({ uri }: { uri: string }) {
  const Pdf = getNativePdf();
  if (!Pdf) {
    return (
      <View style={styles.fallback}>
        <FileText size={42} color={Palette.inkFaint} strokeWidth={1.4} />
        <Text style={styles.fallbackTitle}>Preview not available here</Text>
        <Text style={styles.fallbackBody}>
          Tap &quot;Open&quot; to view this PDF in your device viewer. Inline
          preview arrives with the next app update from the store.
        </Text>
      </View>
    );
  }
  return <Pdf source={{ uri, cache: true }} style={{ flex: 1, backgroundColor: '#1a1714' }} />;
}

/** Synchronously rewrap a base64 data: URL as an object URL — iframes
 *  and window.open handle object URLs far more reliably than data:
 *  navigation for PDFs. */
function toObjectUrl(uri: string): string {
  if (!uri.startsWith('data:')) return uri;
  try {
    const comma = uri.indexOf(',');
    const meta = uri.slice(5, comma);
    const mime = meta.split(';')[0] || 'application/pdf';
    // eslint-disable-next-line no-undef
    const bin = atob(uri.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    // eslint-disable-next-line no-undef
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
  } catch {
    return uri;
  }
}

function PdfWebPreview({ uri }: { uri: string }) {
  // React Native Web doesn't expose an <iframe>. We use the createElement
  // escape hatch only on web.
  if (Platform.OS !== 'web') return null;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const React = require('react');
  return React.createElement('iframe', {
    src: toObjectUrl(uri),
    style: {
      flex: 1,
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: 12,
      backgroundColor: '#fff',
    },
  });
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 480,
    maxHeight: '95%',
    backgroundColor: Palette.sand,
    borderRadius: 22,
    overflow: 'hidden',
    flex: 1,
  },
  sheetWide: {
    // Roughly a portrait Letter page at reading size, without
    // swallowing smaller laptop screens.
    maxWidth: 860,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  kindIcon: {
    width: 38, height: 38, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  title: { fontFamily: Fonts.serifBold, fontSize: 16, color: Palette.ink },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 },
  metaPill: {
    paddingHorizontal: 7, paddingVertical: 2,
    borderRadius: 999, backgroundColor: '#EAF0EA',
  },
  metaPillText: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.forest,
    letterSpacing: 0.4, textTransform: 'uppercase',
  },
  metaText: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, flex: 1 },
  notesText: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  expiresRow: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: Palette.honeySoft,
    paddingVertical: 8, paddingHorizontal: 14,
    borderBottomWidth: 1, borderBottomColor: 'rgba(225,163,61,0.35)',
  },
  expiresText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: '#5A4012',
  },
  previewArea: {
    flex: 1,
    backgroundColor: '#1a1714',
    overflow: 'hidden',
  },
  imageScrollContent: {
    flexGrow: 1, justifyContent: 'center', alignItems: 'center',
    padding: 8,
  },
  image: { width: '100%', height: '100%', minHeight: 320 },
  fallback: {
    flex: 1,
    backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: 24,
  },
  fallbackTitle: { fontFamily: Fonts.serifBold, fontSize: 16, color: Palette.ink },
  fallbackBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 18,
  },
  footer: {
    flexDirection: 'row',
    gap: 8,
    padding: 14,
    borderTopWidth: 1, borderTopColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  footerBtn: {
    flex: 1,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 7, backgroundColor: Palette.forest,
    paddingVertical: 12, borderRadius: 14,
  },
  footerBtnOutline: {
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.forest,
  },
  footerBtnText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
});
